import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Observable } from 'rxjs';
import { groupBy as _groupBy, Dictionary } from 'lodash';

import { PermissionsService, permissionTypes } from '@sd-portal/shared/permissions';

@Component({
  selector: 'sd-portal-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {
  accessAllowedUserManagement: boolean = true;
  routes: DashboardRouteType[];
  routeModules: Dictionary<DashboardRouteType[]>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  keepOriginalOrder = (a: any, _b: any) => a.key;

  constructor(
    private readonly titleService: Title,
    private readonly permissionsService: PermissionsService
  ) {
    this.titleService.setTitle('Dashboard | MySky Scheduler');
  }

  ngOnInit() {
    this.initializeRoutes();
    this.routeModules = _groupBy(this.routes, r => r.module);
  }

  private initializeRoutes() {
    const { profile, preflight, postFlight } = permissionTypes;
    this.routes = [
      // profiles
      {
        module: 'profile',
        value: ['/profile/aircraft'],
        text: 'Aircraft',
        matIcon: 'airplanemode_active',
        accessAllowed$: this.permissionsService.currentUserCan(profile.aircraft.readAccess)
      },
      {
        module: 'profile',
        value: ['/profile/airport'],
        text: 'Airports',
        matIcon: 'flight_takeoff',
        accessAllowed$: this.permissionsService.currentUserCan(profile.airport.readAccess)
      },
      {
        module: 'profile',
        value: ['/profile/company'],
        text: 'Company',
        matIcon: 'location_city',
        accessAllowed$: this.permissionsService.currentUserCan(profile.company.readAccess)
      },
      {
        module: 'profile',
        value: ['/profile/persons'],
        text: 'People',
        matIcon: 'people',
        accessAllowed$: this.permissionsService.currentUserCan(profile.people.readAccess)
      },
      {
        module: 'profile',
        value: ['/profile/training-requirement'],
        text: 'Training Requirement',
        matIcon: 'event_note',
        accessAllowed$: this.permissionsService.currentUserCan(
          profile.trainingRequirement.readAccess
        )
      },
      {
        module: 'profile',
        value: ['/profile/manage-roles'],
        text: 'Roles Management',
        matIcon: 'person',
        accessAllowed$: this.permissionsService.currentUserCan(profile.rolesManagement.readAccess)
      },
      {
        module: 'profile',
        value: ['/profile/user-management'],
        text: 'User Management',
        matIcon: 'contacts',
        accessAllowed$: this.permissionsService.currentUserCan(profile.userManagement.readAccess)
      },
      {
        module: 'profile',
        value: ['/profile/user-signature'],
        text: 'User E-Signature',
        matIcon: 'contacts',
        accessAllowed$: this.permissionsService.currentUserCan(profile.eSignature.writeAccess)
      },
      // pre-flight
      {
        module: 'preflight',
        value: ['/pre-flight/calendar'],
        text: 'Calendar',
        matIcon: 'date_range',
        accessAllowed$: this.permissionsService.currentUserCan(preflight.linkAccess)
      },
      {
        module: 'preflight',
        value: ['/pre-flight/schedule'],
        text: 'Schedule a Trip',
        matIcon: 'schedule',
        accessAllowed$: this.permissionsService.currentUserCan(preflight.buildTrip.writeAccess)
      },

      // post-flight
      {
        module: 'postflight',
        value: ['/post-flight/tripmanager'],
        text: 'Trip Manager',
        matIcon: 'track_changes',
        accessAllowed$: this.permissionsService.currentUserCan(postFlight.tripManager.readAccess)
      },
      {
        module: 'postflight',
        value: ['/post-flight/pilot-logbook'],
        text: 'Pilot Logbook',
        matIcon: 'collections_bookmark',
        accessAllowed$: this.permissionsService.currentUserCan(postFlight.pilotLogbook.readAccess)
      },
      {
        module: 'postflight',
        value: ['/post-flight/expenses'],
        text: 'Expense Capture',
        matIcon: 'monetization_on',
        accessAllowed$: this.permissionsService.currentUserCan(postFlight.pilotLogbook.readAccess)
      },
      {
        module: 'profile',
        value: ['/profile/integration-management'],
        text: 'Integration Management',
        matIcon: 'people',
        accessAllowed$: this.permissionsService.currentUserCan(
          profile.integrationManagement.readAccess
        )
      }
    ];

    this.routes.push({
      module: 'preflight',
      value: ['/pre-flight/schedule-beta'],
      text: 'Bat Beta',
      matIcon: 'schedule',
      accessAllowed$: this.permissionsService.currentUserCan(preflight.buildTrip.writeAccess)
    });
  }
}

export class DashboardRouteType {
  module: 'profile' | 'preflight' | 'postflight';
  value: string[];
  text: string;
  matIcon: string;
  accessAllowed$?: Observable<boolean>;

  constructor(
    module: 'profile' | 'preflight' | 'postflight',
    value: string[],
    text: string,
    matIcon: string,
    accessAllowed: Observable<boolean>
  ) {
    this.module = module;
    this.value = value;
    this.text = text;
    this.matIcon = matIcon;
    this.accessAllowed$ = accessAllowed;
  }
}
